import { useState } from 'react';
import { useSWRConfig } from 'swr';

import { CardHeading } from 'components/Card';
import { EmptyState } from 'components/empty-state/EmptyState';
import { ErrorState } from 'components/error-state/ErrorState';
import { SpinnerBlock } from 'components/Spinner';
import { useTranslation } from 'contexts/translation-context';

import { ConnectionCard } from './ConnectionCard';
import { ConnectionsActions } from './ConnectionsActions';
import { ResizableSidePanel } from './ResizableSidePanel';
import { TruckTrackingPanel } from './TruckTrackingPanel';
import { TrailerTrackingPanel } from './TrailerTrackingPanel';
import { isTelemetryVehiclesKey, useTelemetryConnections, type TelemetryParty, type TelemetryVehicle } from '../models';
import { displayTrailer, type TrailerOption } from 'src/app/trailer/displayTrailer';

/** The reader's chosen width for the side panel, kept across both pages that show one. */
const PANEL_WIDTH_STORAGE_KEY = 'tms.tracking-side-panel.width';

interface IConnectionsPanelProps {
  party?: TelemetryParty;
  /** An internal user owns no party, so they have to name each connection's owner themselves. */
  requiresOwner?: boolean;
  /** Whether this caller may change connections — a driver may only look. */
  canWrite?: boolean;
  /**
   * Heading above the list, in the same bar as the panels around it. Leave it off on a page that draws
   * its own title bar — the portal pages use a `PageHeading`, and then the heading is theirs.
   */
  title?: string;
  subtitle?: string;
}

/** The vehicle whose tracking panel the side column shows, and the connection that reports it. */
type SelectedVehicle =
  | { kind: 'truck'; connectionId: string; vehicleId: string; supplierTruckId: string; name: string }
  | { kind: 'trailer'; connectionId: string; vehicleId: string; trailer: TrailerOption };

/**
 * What a reported unit opens: the trailer it tracks, or the truck it runs in.
 *
 * A unit is linked to one of the two and never both, so only the order of the two checks is a choice — a
 * truck is the platform's own link and leads, a trailer follows it.
 */
const openTargetFor = (connectionId: string, vehicle: TelemetryVehicle): SelectedVehicle | null => {
  if (vehicle.supplierTruckId) {
    return {
      kind: 'truck',
      connectionId,
      vehicleId: vehicle.id,
      supplierTruckId: vehicle.supplierTruckId,
      name: vehicle.supplierTruckName ?? vehicle.licensePlateRaw,
    };
  }

  if (vehicle.trailerId !== null) {
    return {
      kind: 'trailer',
      connectionId,
      vehicleId: vehicle.id,
      trailer: {
        id: vehicle.trailerId,
        name: vehicle.trailerName ?? '',
        licensePlate: vehicle.trailerLicensePlate ?? '',
      },
    };
  }

  return null;
};

/**
 * A party's tracking connections, with the vehicles each reports and what they are linked to.
 *
 * Picking a linked vehicle opens the panel of what it tracks — a truck's position and telemetry, or a
 * trailer's position and the tracker behind it — as a side column beside the list, so the list stays put
 * while you look at one vehicle.
 *
 * Deliberately not wrapped in a `Card`: every connection is one, and a card never nests in a card.
 */
export const ConnectionsPanel = ({
  party,
  requiresOwner = false,
  canWrite = true,
  title,
  subtitle,
}: IConnectionsPanelProps) => {
  const { i18n } = useTranslation();
  const { data: connections, error, isLoading, mutate } = useTelemetryConnections(party);
  const [selected, setSelected] = useState<SelectedVehicle | null>(null);
  const { mutate: mutateAll } = useSWRConfig();

  // A link made from the panel changes a row of the list beside it, and can move the unit of another
  // connection's card — the picker offers the whole fleet. It also changes the count in each card's header,
  // so the list itself is revalidated with the rows.
  const refreshLinks = () => {
    void mutate();
    void mutateAll((key) => isTelemetryVehiclesKey(key, party));
  };

  // Only a unit that is linked has anything to open — the panel is the truck's or the trailer's own — and
  // picking `null` closes it again.
  const selectVehicle = (connectionId: string, vehicle: TelemetryVehicle | null) => {
    setSelected(vehicle ? openTargetFor(connectionId, vehicle) : null);
  };

  return (
    <div className="grid gap-4">
      {title != null && (
        <CardHeading
          title={title}
          subtitle={subtitle}
          rightHeading={<ConnectionsActions party={party} requiresOwner={requiresOwner} canWrite={canWrite} />}
        />
      )}

      {isLoading && !connections && (
        <SpinnerBlock message={i18n('liveTracking.connectionsLoading')} className="min-h-32" />
      )}
      {error && !connections && <ErrorState error={error} onRetry={() => mutate()} />}

      {connections && connections.length === 0 && (
        <EmptyState
          variant="panel"
          title={i18n('liveTracking.noConnections')}
          description={i18n('liveTracking.noConnectionsHint')}
        />
      )}

      {connections && connections.length > 0 && (
        // The panel is a column of its own, reserved only once a vehicle is picked — until then the list
        // keeps the full width — and the divider between the two lets the reader widen it. Narrower than
        // `xl`, where the columns would be too cramped, the two stack and there is nothing to divide.
        <ResizableSidePanel
          storageKey={PANEL_WIDTH_STORAGE_KEY}
          side={
            selected ? (
              selected.kind === 'truck' ? (
                <TruckTrackingPanel
                  // Keyed on the truck so a new pick starts a fresh panel: live tracking is switched on per view,
                  // and a truck the reader never asked about must not inherit another truck's 30s polling.
                  key={selected.supplierTruckId}
                  supplierTruckId={selected.supplierTruckId}
                  party={party}
                  title={selected.name}
                  emptyMessage={i18n('liveTracking.noPosition')}
                  // This panel is the one the reader can widen, so its map may grow with it.
                  resizableWidth
                />
              ) : (
                <TrailerTrackingPanel
                  // Keyed on the trailer, for the same reason the truck panel is: polling is per view.
                  key={selected.trailer.id}
                  trailer={selected.trailer}
                  party={party}
                  title={displayTrailer(selected.trailer)}
                  canLink={canWrite}
                  // Re-linking here changes the rows and the count of the list this panel sits beside.
                  onLinked={refreshLinks}
                  resizableWidth
                />
              )
            ) : undefined
          }
        >
          {connections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              party={party}
              canWrite={canWrite}
              onChanged={() => mutate()}
              selectedVehicleId={selected?.connectionId === connection.id ? selected.vehicleId : null}
              onSelectVehicle={(vehicle) => selectVehicle(connection.id, vehicle)}
            />
          ))}
        </ResizableSidePanel>
      )}
    </div>
  );
};
